import { useEffect } from "react";
import { Link, NavLink, Outlet, useLocation, useSearchParams } from "react-router";
import { toast } from "sonner";
import { InstallPrompt } from "@/components/install-prompt";
import { PlayerSearch } from "@/components/player-search";
import { useQueryClient } from "@tanstack/react-query";
import { Film, LayoutGrid, LogOut, Shield, Sparkles, User as UserIcon } from "lucide-react";
import { Logo, PlayerAvatar } from "@/components/cs";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useMe } from "@/hooks/use-me";
import { api, type User } from "@/lib/api";
import { cn } from "@/lib/utils";

function navItems(me: User) {
  return [
    { to: "/", label: "Матчи", icon: LayoutGrid, end: true },
    { to: "/videos", label: "Видео", icon: Film },
    { to: `/player/${me.steam_id}`, label: "Профиль", icon: UserIcon },
    { to: "/connect", label: "ИИ", icon: Sparkles },
    ...(me.role === "admin" ? [{ to: "/admin", label: "Админка", icon: Shield }] : []),
  ];
}

const tabItems = (me: User) => navItems(me).filter((i) => i.to !== "/admin");

export function SteamLoginButton({ size = "default" }: { size?: "default" | "lg" }) {
  return (
    <Button asChild size={size} className="font-semibold">
      <a href="/auth/steam/login">Войти через Steam</a>
    </Button>
  );
}

export function FaceitLoginButton({ size = "default", label = "Войти через FACEIT" }: { size?: "default" | "lg" | "sm"; label?: string }) {
  return (
    <Button asChild size={size} variant="outline" className="border-[#ff5500]/60 font-semibold text-[#ff5500] hover:bg-[#ff5500]/10 hover:text-[#ff5500]">
      <a href="/auth/faceit/login">{label}</a>
    </Button>
  );
}

const FACEIT_RESULT: Record<string, [string, "success" | "error"]> = {
  linked: ["FACEIT привязан", "success"],
  "signed-in": ["Вы вошли через FACEIT", "success"],
  taken: ["Этот FACEIT уже привязан к другому аккаунту", "error"],
  nosteam: ["В этом FACEIT-аккаунте не привязан Steam — войдите через Steam", "error"],
  expired: ["Вход через FACEIT устарел, попробуйте ещё раз", "error"],
  canceled: ["Вход через FACEIT отменён", "error"],
  error: ["FACEIT не подтвердил вход, попробуйте позже", "error"],
};

/** Shows the result of a FACEIT login redirect (?faceit=...) once. */
function useFaceitResult() {
  const [params, setParams] = useSearchParams();
  const result = params.get("faceit");
  useEffect(() => {
    if (!result) return;
    const [text, kind] = FACEIT_RESULT[result] ?? FACEIT_RESULT.error;
    if (kind === "success") toast.success(text);
    else toast.error(text);
    params.delete("faceit");
    setParams(params, { replace: true });
  }, [result, params, setParams]);
}

function useLogout() {
  const qc = useQueryClient();
  return async () => {
    await api("/auth/logout", { method: "POST" });
    qc.clear();
    location.href = "/";
  };
}

export function AppLayout() {
  useFaceitResult();
  const { data: me } = useMe();
  const logout = useLogout();
  const { pathname } = useLocation();

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="safe-top sticky top-0 z-40 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-4 px-4">
          <Link to="/" aria-label="На главную">
            <Logo />
          </Link>

          {me && (
            <nav className="hidden flex-1 items-center gap-1 md:flex">
              {navItems(me).map(({ to, label, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    cn(
                      "relative rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground",
                      "after:absolute after:inset-x-3 after:-bottom-[13px] after:h-0.5 after:rounded-full after:bg-primary after:opacity-0 after:transition-opacity",
                      isActive && "text-foreground after:opacity-100",
                    )
                  }
                >
                  {label}
                </NavLink>
              ))}
            </nav>
          )}
          <div className="ml-auto flex items-center gap-2">
            {me && <PlayerSearch />}
            {!me ? (
              <SteamLoginButton />
            ) : (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 rounded-full py-1 pl-1 pr-1 hover:bg-secondary md:rounded-md md:pr-2">
                      <PlayerAvatar name={me.display_name} src={me.avatar_animated ?? me.avatar_url} className="size-8 rounded-full md:size-7 md:rounded-md" />
                      <span className="hidden max-w-40 truncate text-sm md:inline">{me.display_name}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel className="truncate">{me.display_name}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to={`/player/${me.steam_id}`}><UserIcon className="size-4" /> Мой профиль</Link>
                    </DropdownMenuItem>
                    {me.role === "admin" && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin"><Shield className="size-4" /> Админка</Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout}>
                      <LogOut className="size-4" /> Выйти
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>


              </>
            )}
          </div>
        </div>
      </header>

      <main key={pathname.split("/")[1]} className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 animate-in fade-in-0 duration-300">
        <Outlet />
      </main>
      {me && <InstallPrompt />}

      <footer className={cn("flex flex-wrap justify-center gap-x-4 gap-y-1 border-t py-6 text-center text-xs text-muted-foreground", me && "pb-24 md:pb-6")}>
        <span>cscoach · разбор демок CS2</span>
        <Link to="/privacy" className="hover:text-foreground">Конфиденциальность</Link>
        <Link to="/terms" className="hover:text-foreground">Условия</Link>
      </footer>

      {/* Phone: app-style tab bar */}
      {me && (
        <nav className="safe-bottom fixed inset-x-0 bottom-0 z-40 border-t bg-background/85 backdrop-blur md:hidden">
          {/* The admin panel stays in the avatar menu: five tabs do not fit a phone. */}
          <div className="mx-auto grid max-w-md" style={{ gridTemplateColumns: `repeat(${tabItems(me).length}, minmax(0, 1fr))` }}>
            {tabItems(me).map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn("relative flex flex-col items-center gap-1 py-2.5 text-[11px] text-muted-foreground transition-colors", isActive && "text-primary")
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && <span className="absolute inset-x-6 top-0 h-0.5 rounded-full bg-primary" />}
                    <Icon className="size-5" />
                    {label}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}
